<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { listaIdiomas } from '@/utils/listaIdiomas'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idListaClienteCabecalho: 0,
      idOrganizacao: null, // GUARDIÃO: Adicionado ao modelo vazio
      nomeLista: '',
      descricaoLista: '',
      prefixoItemLista: '',
      permitePersonalizacao: true,
      exclusivaCliente: true,
      isListaEstrutural: null,
      especificacaoTecnica: ''
    })
  }
})

const emit = defineEmits([
  'itemAtualizado',
  'cancelar',
  'changesDetected',
  'show-notification'
])

const authStore = useAuthStore()

// --- Estado Local Essencial ---
const isNew = computed(() => !props.itemParaEditar)

const temPermissaaoCompleta = computed(() => authStore.pode('LISTAS_CLIENTE_COMPLETO'))

// GUARDIÃO: Avalia se o formulário inteiro deve ser travado (Apenas visualização)
const isReadOnly = computed(() => {
  if (isEditing.value && item.value.permitePersonalizacao === false && !authStore.isAdminInterno) return true
  return isEditing.value && !temPermissaaoCompleta.value
})

const tituloFormulario = computed(() => {
  if (isNew.value) return 'Nova Lista Personalizada'
  if (isReadOnly.value) return 'Visualizar Lista'
  return 'Editar Lista Personalizada'
})

// --- Lógica do Cabeçalho ---
// GUARDIÃO: Validação Híbrida 100% automática (DI)
const {
  item,
  isLoading: isSavingCabecalho,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

// --- Lógica de Travas ---
const isHeaderSaved = ref(isEditing.value)
const isSavingHeaderLocal = ref(false)
const isItensFrameBlocked = computed(() => !isHeaderSaved.value)

// GUARDIÃO: Regra de Edição Inteligente dos Dados Básicos
const isHeaderReadOnly = computed(() => {
  // Se o form inteiro é ReadOnly, o cabeçalho também é
  if (isReadOnly.value) return true

  // Se já está salvo (edição) e NÃO é exclusiva do cliente, bloqueia a edição do cabeçalho
  if (isEditing.value && !item.value.exclusivaCliente) return true

  // Se for exclusiva do cliente (criada por ele), os Dados Básicos ficam editáveis
  return false
})

const opcoesStatusItem = [
  { id: true, label: 'Ativo' },
  { id: false, label: 'Inativo' }
]

// --- Filtro de Idioma (CampoBuscaLista) ---
const idiomaFiltro = ref('pt-BR')
const idiomaFiltroCodigo = ref('LST_IDIOMAS1')
const isIdiomaFiltroReadOnly = computed(() => idiomaFiltro.value !== 'pt-BR')

function onIdiomaSelecionado (item) {
  idiomaFiltro.value = item.textoLista || 'pt-BR'
  idiomaFiltroCodigo.value = item.idListaSistemaItem || 'LST_IDIOMAS1'
}

// --- Modal de Variantes de Idioma ---
const modalIdiomasAberto = ref(false)
const codigoSelecionadoParaIdiomas = ref('')
const prefixoSelecionadoParaIdiomas = ref('')

const itensDoCodigo = computed(() => {
  if (!codigoSelecionadoParaIdiomas.value) return []
  return itensDaLista.value.filter(i =>
    i.idListaClienteItem === codigoSelecionadoParaIdiomas.value
  )
})

const idiomasDisponiveisParaCodigo = computed(() => {
  const presentes = new Set(itensDoCodigo.value.map(i => i.idioma))
  return listaIdiomas.filter(li => !presentes.has(li.id))
})

const novoItemIdioma = ref({
  textoLista: '',
  descricaoItem: '',
  ativo: true,
  idioma: ''
})

function abrirModalIdiomas (itemLista) {
  codigoSelecionadoParaIdiomas.value = itemLista.idListaClienteItem
  prefixoSelecionadoParaIdiomas.value = itemLista.prefixoItemLista || item.value.prefixoItemLista
  novoItemIdioma.value = { textoLista: '', descricaoItem: '', ativo: true, idioma: '' }
  modalIdiomasAberto.value = true
}

function fecharModalIdiomas () {
  modalIdiomasAberto.value = false
  codigoSelecionadoParaIdiomas.value = ''
  prefixoSelecionadoParaIdiomas.value = ''
}

async function salvarItemIdioma () {
  if (!novoItemIdioma.value.textoLista) {
    emit('show-notification', { message: 'O campo "Texto do Item" é obrigatório.', type: 'error' })
    return
  }
  if (!novoItemIdioma.value.idioma) {
    emit('show-notification', { message: 'Selecione um idioma.', type: 'error' })
    return
  }

  const dto = {
    textoLista: novoItemIdioma.value.textoLista,
    descricaoItem: novoItemIdioma.value.descricaoItem,
    ativo: novoItemIdioma.value.ativo,
    idioma: novoItemIdioma.value.idioma,
    idItem: codigoSelecionadoParaIdiomas.value
  }

  try {
    const response = await api.post(`/ListasCliente/itens/${prefixoSelecionadoParaIdiomas.value}`, dto)
    itensDaLista.value.push(response.data)
    novoItemIdioma.value = { textoLista: '', descricaoItem: '', ativo: true, idioma: '' }
    emit('show-notification', { message: `Item adicionado em "${novoItemIdioma.value.idioma}".`, type: 'success' })
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || 'Erro ao adicionar variante de idioma.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

async function salvarEdicaoItemIdioma (itemParaSalvar) {
  if (!itemParaSalvar.textoLista) {
    emit('show-notification', { message: 'O campo "Texto do Item" é obrigatório.', type: 'error' })
    return
  }

  const dto = {
    textoLista: itemParaSalvar.textoLista,
    descricaoItem: itemParaSalvar.descricaoItem,
    ativo: itemParaSalvar.ativo,
    idioma: itemParaSalvar.idioma
  }

  try {
    await api.put(`/ListasCliente/itens/${itemParaSalvar.idSequenciaTabela}`, dto)
    const idx = itensDaLista.value.findIndex(i => i.idSequenciaTabela === itemParaSalvar.idSequenciaTabela)
    if (idx !== -1) {
      itensDaLista.value[idx] = { ...itensDaLista.value[idx], ...dto }
    }
    emit('show-notification', { message: 'Item atualizado.', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || 'Erro ao atualizar item.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- Inativação em Cascata ---
async function inativarCascata (itemLista) {
  const novoStatus = !itemLista.ativo
  const acao = novoStatus ? 'reativar' : 'inativar'

  try {
    await api.patch(`/ListasCliente/itens/${itemLista.idSequenciaTabela}/status`, { ativo: novoStatus })
    emit('show-notification', { message: `Item ${acao}do em todos os idiomas.`, type: 'success' })
    await buscarItens()
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || `Erro ao ${acao} o item.`
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- Lógica do Modal de Auditoria ---
const mostrarAuditoria = ref(false)
const abrirAuditoria = () => {
  mostrarAuditoria.value = true
}

// --- Lógica dos Itens (CRUD Embutido) ---
const itensDaLista = ref([])
const isLoadingItens = ref(false)
const erroItens = ref(null)
const itemEmEdicao = ref(null)
const isSavingItem = ref(false)

const modeloVazioItem = () => ({
  idSequenciaTabela: null,
  idListaClienteCabecalho: item.value.idListaClienteCabecalho,
  idListaClienteItem: '',
  textoLista: '',
  descricaoItem: '',
  ativo: true,
  isSystem: false,
  idioma: 'pt-BR'
})

if (isNew.value) {
  item.value = {
    idListaClienteCabecalho: 0,
    idOrganizacao: null,
    prefixoItemLista: '',
    nomeLista: '',
    descricaoLista: '',
    permitePersonalizacao: true,
    exclusivaCliente: true
  }
}

watch(item, () => {
  emit('changesDetected', true)
}, { deep: true })

async function buscarItens () {
  if (!isEditing.value || !item.value.prefixoItemLista) return
  isLoadingItens.value = true
  erroItens.value = null
  try {
    const url = `/ListasCliente/itens/${item.value.prefixoItemLista}?includeInactive=true`
    const response = await api.get(url)
    itensDaLista.value = response.data
  } catch (error) {
    console.error('Erro ao buscar itens:', error)
    erroItens.value = 'Falha ao carregar os itens da lista.'
  } finally {
    isLoadingItens.value = false
  }
}

function iniciarNovoItem () {
  if (isReadOnly.value) return
  itemEmEdicao.value = modeloVazioItem()
}

function iniciarEdicaoItem (itemOriginal) {
  if (isReadOnly.value) return

  if (itemOriginal.isSystem && !authStore.isAdminInterno) {
    emit('show-notification', { message: 'Itens padrão do sistema não podem ser editados.', type: 'warning' })
    return
  }

  itemEmEdicao.value = { ...itemOriginal }
}

function cancelarEdicaoItem () {
  itemEmEdicao.value = null
}

async function salvarSubItem (itemParaSalvar) {
  if (isReadOnly.value) return
  if (!itemParaSalvar.textoLista) {
    emit('show-notification', { message: 'O campo "Texto do Item" é obrigatório.', type: 'error' })
    return
  }

  isSavingItem.value = true

  const dto = {
    textoLista: itemParaSalvar.textoLista,
    descricaoItem: itemParaSalvar.descricaoItem,
    ativo: itemParaSalvar.ativo,
    isSystem: false,
    idioma: itemParaSalvar.idioma || 'pt-BR'
  }

  try {
    if (itemParaSalvar.idSequenciaTabela) {
      const url = `/ListasCliente/itens/${itemParaSalvar.idSequenciaTabela}`
      await api.put(url, dto)
      emit('show-notification', { message: 'Item atualizado com sucesso.', type: 'success' })
    } else {
      const url = `/ListasCliente/itens/${item.value.prefixoItemLista}`
      await api.post(url, dto)
      emit('show-notification', { message: 'Item criado com sucesso.', type: 'success' })
    }

    cancelarEdicaoItem()
    await buscarItens()
  } catch (error) {
    console.error('Erro ao salvar item:', error)
    const errorMsg = error.response?.data?.message || 'Erro ao salvar o item.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isSavingItem.value = false
  }
}

async function salvarCabecalhoLocalmente () {
  if (isHeaderReadOnly.value) return
  isSavingHeaderLocal.value = true

  // GUARDIÃO: Injeta forçosamente a empresa ativa
  const payload = {
    nomeLista: item.value.nomeLista,
    descricaoLista: item.value.descricaoLista,
    prefixoItemLista: item.value.prefixoItemLista ? item.value.prefixoItemLista.toUpperCase() : '',
    permitePersonalizacao: true
  }

  try {
    const apiUrl = '/ListasCliente'
    const response = await api.post(apiUrl, payload)

    item.value = response.data

    isHeaderSaved.value = true
    isEditing.value = true
    entityInfo.value = `Editando ID: ${item.value.idListaClienteCabecalho}`

    emit('show-notification', { message: 'Cabeçalho salvo. Agora pode adicionar itens.', type: 'success' })
    await buscarItens()
  } catch (error) {
    console.error('Erro ao salvar cabeçalho localmente:', error)
    const errorMsg = error.response?.data?.message || 'Erro ao salvar o cabeçalho.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isSavingHeaderLocal.value = false
  }
}

async function submitFormDoCabecalho () {
  if (isHeaderReadOnly.value) return

  // GUARDIÃO: Injeta forçosamente a empresa ativa
  const payload = {
    nomeLista: item.value.nomeLista,
    descricaoLista: item.value.descricaoLista,
    prefixoItemLista: item.value.prefixoItemLista ? item.value.prefixoItemLista.toUpperCase() : '',
    permitePersonalizacao: item.value.permitePersonalizacao,
    isListaEstrutural: item.value.isListaEstrutural,
    especificacaoTecnica: item.value.especificacaoTecnica
  }

  await submitForm(
    'ListasCliente',
    'idListaClienteCabecalho',
    payload
  )
}

onMounted(async () => {
  if (isEditing.value) {
    buscarItens()
  }
})
</script>

<template>
  <FormularioCadastro
    :module-name="tituloFormulario"
    :is-loading="isSavingCabecalho"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="submitFormDoCabecalho"
    @cancelar="handleCancel"
    @show-notification="(payload) => emit('show-notification', payload)"
    :desabilitar-salvar="isHeaderReadOnly"
    :ocultar-salvar="!isHeaderSaved || isHeaderReadOnly"
  >
    <!-- GUARDIÃO: Botão de Acesso ao Log -->
    <template #acoes-header v-if="isEditing">
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="abrirAuditoria"
        title="Ver Histórico de Alterações"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="isReadOnly" class="alert alert-info border-0 shadow-sm d-flex align-items-center mb-4">
      <i class="bi bi-info-circle-fill fs-5 me-3 text-info"></i>
      <div>
        <strong>Modo de Visualização</strong>
        <p class="mb-0 small">Esta lista é protegida pelo sistema. Não é permitida a adição ou modificação de itens pelo utilizador.</p>
      </div>
    </div>

    <!-- Frame 1: Dados Básicos -->
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Dados Básicos</h5>
        <span v-if="item.exclusivaCliente" class="badge bg-success-subtle text-success-emphasis border border-success-subtle">
          <i class="bi bi-star-fill me-1"></i> Lista Exclusiva
        </span>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-8">
            <!-- GUARDIÃO: ID exatamente igual ao v-model (nomeLista) -->
            <CampoInput
              id="nomeLista"
              label="Nome da Lista"
              v-model="item.nomeLista"
              placeholder='Ex: "Categoria de Fornecedor"'
              required
              :readonly="isHeaderReadOnly"
            />
          </div>

          <div class="col-md-4">
            <!-- GUARDIÃO: ID exatamente igual ao v-model (prefixoItemLista) -->
            <CampoInput
              id="prefixoItemLista"
              label="Prefixo (Código)"
              v-model="item.prefixoItemLista"
              placeholder='Ex: "CAT_FORN"'
              required
              maxlength="50"
              :readonly="isHeaderReadOnly"
              :disabled="!isNew"
              @input="item.prefixoItemLista = item.prefixoItemLista.toUpperCase()"
            />
          </div>

          <div v-show="!item.isListaEstrutural" class="col-12">
            <!-- GUARDIÃO: ID exatamente igual ao v-model (descricaoLista) -->
            <CampoInput
              id="descricaoLista"
              label="Descrição"
              type="textarea"
              v-model="item.descricaoLista"
              :readonly="isHeaderReadOnly"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="mb-4" v-if="!isHeaderSaved && !isReadOnly">
      <button
        type="button"
        class="btn btn-success px-4"
        @click="salvarCabecalhoLocalmente"
        :disabled="isSavingHeaderLocal || !item.nomeLista || !item.prefixoItemLista"
      >
        <span v-if="isSavingHeaderLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-save me-2"></i>
        Salvar Cabeçalho e Liberar Itens
      </button>
    </div>

    <fieldset :disabled="isItensFrameBlocked">
      <div class="card" :class="{'frame-bloqueado': isItensFrameBlocked}">
        <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div class="d-flex align-items-center flex-wrap gap-2">
            <h5 class="mb-0 me-3">Itens da Lista</h5>

            <!-- Filtro de Idioma -->
            <div v-show="!isItensFrameBlocked" class="d-inline-flex align-items-center">
              <CampoBuscaLista
                id="filtroIdioma"
                label="Idioma"
                prefixo="LST_IDIOMAS"
                contexto="Cliente"
                :model-value="idiomaFiltroCodigo"
                hide-label
                style="max-width: 200px;"
                @selecionado="onIdiomaSelecionado"
              />
            </div>

            <button
              v-if="!isReadOnly && !isIdiomaFiltroReadOnly"
              type="button"
              class="btn btn-primary btn-sm"
              @click="iniciarNovoItem"
              :disabled="!!itemEmEdicao"
            >
              <i class="bi bi-plus-lg me-1"></i> Adicionar Item
            </button>
          </div>
        </div>

        <div class="card-body">

          <div v-if="isItensFrameBlocked && !isReadOnly" class="alert alert-info border-0 shadow-sm text-center">
            <i class="bi bi-info-circle me-2"></i>
            Salve os Dados Básicos (acima) para adicionar ou editar itens.
          </div>

          <div v-else-if="isLoadingItens" class="text-center text-muted py-5">
            <span class="spinner-border spinner-border-sm me-2"></span>
            A carregar itens...
          </div>

          <div v-else-if="erroItens" class="alert alert-danger border-0 shadow-sm">{{ erroItens }}</div>

          <div v-else class="admin-table-container">
            <table class="table admin-table">
              <thead>
                <tr>
                  <th style="width: 150px;">Código</th>
                  <th style="width: 30%;">Texto do Item</th>
                  <th>Descrição Detalhada</th>
                  <th style="width: 80px;" class="text-center">Idioma</th>
                  <th style="width: 100px;" class="text-center">Status</th>
                  <th class="text-end" style="width: 120px;" v-if="!isReadOnly">Ações</th>
                </tr>
              </thead>

              <tbody>

                <!-- Linha de Novo Item -->
                <tr v-if="itemEmEdicao && !itemEmEdicao.idSequenciaTabela" class="linha-edicao">
                  <td>
                    <CampoInput :model-value="item.prefixoItemLista + '...'" readonly disabled hide-label id="novoCodigo" />
                  </td>

                  <td>
                    <CampoInput v-model="itemEmEdicao.textoLista" hide-label id="novoTextoLista" />
                  </td>

                  <td>
                    <CampoInput v-model="itemEmEdicao.descricaoItem" hide-label id="novoDescricaoItem" />
                  </td>

                  <td>
                    <CampoSelect
                      id="novoItemAtivo"
                      v-model="itemEmEdicao.ativo"
                      :options="opcoesStatusItem"
                      option-value="id"
                      option-label="label"
                      hide-label
                    />
                  </td>

                  <td class="acoes-cell">
                    <div class="acoes-wrapper justify-content-end">
                      <button
                        type="button"
                        class="btn btn-success btn-sm me-1"
                        @click="salvarSubItem(itemEmEdicao)"
                        :disabled="isSavingItem"
                      >
                        <i class="bi bi-check-lg"></i>
                      </button>

                      <button
                        type="button"
                        class="btn btn-secondary btn-sm"
                        @click="cancelarEdicaoItem"
                        :disabled="isSavingItem"
                      >
                        <i class="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="itensDaLista.length === 0 && !itemEmEdicao">
                  <td :colspan="isReadOnly ? 4 : 5" class="text-center text-muted py-4">Nenhum item adicionado a esta lista.</td>
                </tr>

                <template v-for="itemLista in itensDaLista" :key="itemLista.idSequenciaTabela">

                  <!-- Linha de Edição (Item Existente) -->
                  <tr
                    v-if="itemEmEdicao && itemEmEdicao.idSequenciaTabela === itemLista.idSequenciaTabela"
                    class="linha-edicao"
                  >
                    <td>
                      <CampoInput :model-value="itemLista.idListaClienteItem" readonly disabled hide-label :id="'codigo' + itemLista.idSequenciaTabela" />
                    </td>

                    <td>
                      <CampoInput v-model="itemEmEdicao.textoLista" hide-label :id="'textoLista' + itemLista.idSequenciaTabela" />
                    </td>

                    <td>
                      <CampoInput v-model="itemEmEdicao.descricaoItem" hide-label :id="'descricaoItem' + itemLista.idSequenciaTabela" />
                    </td>

                    <td>
                      <CampoSelect
                        :id="'itemAtivo' + itemLista.idSequenciaTabela"
                        v-model="itemEmEdicao.ativo"
                        :options="opcoesStatusItem"
                        option-value="id"
                        option-label="label"
                        hide-label
                      />
                    </td>

                    <td class="acoes-cell">
                      <div class="acoes-wrapper justify-content-end">
                        <button
                          type="button"
                          class="btn btn-success btn-sm me-1"
                          @click="salvarSubItem(itemEmEdicao)"
                          :disabled="isSavingItem"
                        >
                          <i class="bi bi-check-lg"></i>
                        </button>

                        <button
                          type="button"
                          class="btn btn-secondary btn-sm"
                          @click="cancelarEdicaoItem"
                          :disabled="isSavingItem"
                        >
                          <i class="bi bi-x-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>

                  <!-- Linha de Visualização -->
                  <tr v-else v-show="itemLista.idioma === idiomaFiltro" :class="{'bg-body-tertiary': itemLista.isSystem}">
                    <td>
                      {{ itemLista.idListaClienteItem }}
                      <i v-if="itemLista.isSystem" class="bi bi-lock-fill text-warning ms-2" title="Item de Sistema (Protegido)"></i>
                    </td>

                    <td class="fw-bold">{{ itemLista.textoLista }}</td>
                    <td class="text-muted small">{{ itemLista.descricaoItem || '-' }}</td>

                    <td class="text-center text-muted small">
                      {{ itemLista.idioma || '-' }}
                    </td>

                    <td class="text-center">
                      <div class="form-check form-switch d-inline-flex align-items-center p-0 m-0" style="min-height: 0;">
                        <input
                          class="form-check-input cursor-pointer"
                          type="checkbox"
                          :id="'switchAtivo_' + itemLista.idSequenciaTabela"
                          :checked="itemLista.ativo"
                          :disabled="!!itemEmEdicao || itemLista.isSystem || isIdiomaFiltroReadOnly"
                          @change="inativarCascata(itemLista)"
                        />
                        <label class="form-check-label small cursor-pointer ms-1 mb-0" :for="'switchAtivo_' + itemLista.idSequenciaTabela">
                          {{ itemLista.ativo ? 'Ativo' : 'Inativo' }}
                        </label>
                      </div>
                    </td>

                    <td class="acoes-cell" v-if="!isReadOnly">
                      <div class="acoes-wrapper justify-content-end">
                        <button
                          type="button"
                          class="btn-icon text-primary"
                          @click="iniciarEdicaoItem(itemLista)"
                          :disabled="!!itemEmEdicao || itemLista.isSystem || isIdiomaFiltroReadOnly"
                          :title="itemLista.isSystem ? 'Item de Sistema (Protegido)' : 'Editar Item'"
                          :style="itemLista.isSystem ? 'opacity: 0.5; cursor: not-allowed;' : ''"
                        >
                          <i class="bi bi-pencil-fill"></i>
                        </button>

                        <button
                          type="button"
                          class="btn-icon text-info"
                          @click="abrirModalIdiomas(itemLista)"
                          :disabled="!!itemEmEdicao || itemLista.isSystem"
                          title="Variantes de idioma"
                        >
                          <i class="bi bi-translate"></i>
                        </button>
                      </div>
                    </td>
                  </tr>

                </template>

              </tbody>
            </table>
          </div>

        </div>
      </div>
    </fieldset>

  </FormularioCadastro>

  <!-- GUARDIÃO: O Modal do Log de Auditoria direcionado para o Tenant -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ListaClienteCabecalho"
    contexto="tenant"
    :chave-primaria="item.idListaClienteCabecalho"
    :tabelas-filhas="['ListaClienteItens']"
    chave-estrangeira="IdListaClienteCabecalho"
    :titulo-registro="item.nomeLista || 'Lista Personalizada'"
    @fechar="mostrarAuditoria = false"
  />

  <!-- Modal de Variantes de Idioma -->
  <ModalFormulario
    v-if="modalIdiomasAberto"
    v-model="modalIdiomasAberto"
    :titulo="'Variantes de idioma — ' + codigoSelecionadoParaIdiomas"
    tamanho="xl"
    :ocultar-confirmar="true"
    texto-confirmar="Fechar"
    @update:model-value="fecharModalIdiomas"
  >
    <div class="mb-3">
      <p class="text-muted small mb-2">
        <i class="bi bi-info-circle me-1"></i>
        Gerencie as traduções do item <strong>{{ codigoSelecionadoParaIdiomas }}</strong>.
        Adicione novos idiomas ou edite os existentes. A exclusão não é permitida.
      </p>

      <!-- Grid de idiomas existentes -->
      <div class="table-responsive" style="max-height: 250px; overflow-y: auto;">
        <table class="table table-sm table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th style="width: 100px;">Idioma</th>
              <th>Texto do Item</th>
              <th>Descrição</th>
              <th style="width: 80px;" class="text-center">Status</th>
              <th class="text-end" style="width: 60px;">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="itemIdioma in itensDoCodigo" :key="itemIdioma.idSequenciaTabela">
              <td>
                <span class="badge bg-info-subtle text-info-emphasis">{{ itemIdioma.idioma }}</span>
              </td>
              <td>
                <CampoInput
                  :id="'modalTexto_' + itemIdioma.idSequenciaTabela"
                  v-model="itemIdioma.textoLista"
                  hide-label
                  size="sm"
                />
              </td>
              <td>
                <CampoInput
                  :id="'modalDesc_' + itemIdioma.idSequenciaTabela"
                  v-model="itemIdioma.descricaoItem"
                  hide-label
                  size="sm"
                />
              </td>
              <td class="text-center">
                <CampoSelect
                  :id="'modalAtivo_' + itemIdioma.idSequenciaTabela"
                  v-model="itemIdioma.ativo"
                  :options="opcoesStatusItem"
                  option-value="id"
                  option-label="label"
                  hide-label
                />
              </td>
              <td class="text-end">
                <button
                  type="button"
                  class="btn btn-success btn-sm"
                  @click="salvarEdicaoItemIdioma(itemIdioma)"
                  title="Salvar alterações"
                >
                  <i class="bi bi-check-lg"></i>
                </button>
              </td>
            </tr>
            <tr v-if="itensDoCodigo.length === 0">
              <td colspan="5" class="text-center text-muted py-3">
                Nenhuma variante de idioma encontrada para este código.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <hr class="my-3" />

      <!-- Adicionar novo idioma -->
      <div class="row g-2 align-items-center">
        <div class="col-md-3">
          <CampoSelect
            id="novoIdiomaModal"
            label="Novo Idioma"
            v-model="novoItemIdioma.idioma"
            :options="idiomasDisponiveisParaCodigo"
            option-value="id"
            option-label="label"
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="novoTextoModal"
            label="Texto do Item"
            v-model="novoItemIdioma.textoLista"
          />
        </div>
        <div class="col-md-4">
          <CampoInput
            id="novoDescModal"
            label="Descrição"
            v-model="novoItemIdioma.descricaoItem"
          />
        </div>
        <div class="col-md-2">
          <button
            type="button"
            class="btn btn-primary w-100"
            @click="salvarItemIdioma"
            :disabled="!novoItemIdioma.idioma || !novoItemIdioma.textoLista"
          >
            <i class="bi bi-plus-lg me-1"></i> Adicionar
          </button>
        </div>
      </div>
    </div>
  </ModalFormulario>
</template>

<style scoped>

.acoes-cell {
  padding-right: 0.75rem;
}

.acoes-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
}

.frame-bloqueado {
  opacity: 0.7;
  background-color: var(--bs-secondary-bg);
  pointer-events: none;
  border-radius: 0.375rem;
}

/* --- Estilos da Tabela Embutida --- */
.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.375rem;
  height: 325px;
  overflow-y: auto;
  overflow-x: auto;
}

.table.admin-table {
  margin-bottom: 0;
  border-collapse: collapse;
  table-layout: auto;
  color: var(--bs-body-color);
}

.admin-table thead th {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-heading-color);
  border-bottom-width: 2px;
  border-bottom-color: var(--bs-border-color-translucent);
  vertical-align: middle;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  height: 45px;
  position: sticky;
  top: 0;
  z-index: 1;
}

.admin-table tbody {
  min-height: 280px;
}

.admin-table td, .admin-table th {
  padding: 0.25rem 0.75rem;
  height: 40px;
  vertical-align: middle;
  border-bottom: 1px solid var(--bs-border-color-translucent);
  border-right: 1px solid var(--bs-border-color-translucent);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background-color 0.2s ease-out;
}

.admin-table th:nth-child(2),
.admin-table td:nth-child(2),
.admin-table th:nth-child(3),
.admin-table td:nth-child(3) {
  white-space: normal;
  width: auto;
}

.admin-table th:last-child,
.admin-table td:last-child {
  border-right: none;
}

/* Fundo diferenciado para a linha em edição */
.admin-table tr.linha-edicao td {
  background-color: var(--bs-tertiary-bg) !important;
  padding-top: 0.15rem;
  padding-bottom: 0.15rem;
}

:deep(td .form-label) {
  display: none;
}

.admin-table tbody tr:not(.linha-edicao):hover td {
  background-color: var(--bs-secondary-bg);
}

.acoes-cell {
  text-align: right;
  position: relative;
}
.acoes-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s ease-out;
}
tr:not(.linha-edicao):hover .acoes-wrapper,
tr.linha-edicao .acoes-wrapper {
  opacity: 1;
}

.btn-icon {
  background: none;
  border: none;
  color: #6c7d8e;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  line-height: 1;
  transition: background-color 0.2s, color 0.2s;
}
.btn-icon:hover {
  background-color: #e2e6ea;
  color: #212934;
}

[data-bs-theme="dark"] .btn-icon:hover {
  background-color: #343a40;
  color: #fff;
}

.btn-icon i {
  font-size: 0.9rem;
  vertical-align: middle;
}
</style>
